import { useEffect, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import NebulaFlowCursor from "./NebulaFlowCursor";

// Matrix Rain Effect
const MatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const chars = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ$€£¥₺";
    const charArray = chars.split("");
    const fontSize = 16;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns).fill(1).map(() => Math.random() * -100);
    
    const draw = () => {
      ctx.fillStyle = "rgba(0, 0, 0, 0.04)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const text = charArray[Math.floor(Math.random() * charArray.length)];
        ctx.fillStyle = "#00FF94";
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        
        if (Math.random() > 0.975) {
          ctx.fillStyle = "#FFFFFF";
          ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        }
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.95) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 40);
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener("resize", handleResize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.2 }}
    />
  );
};

// Snowfall Effect for Christmas
const Snowfall = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const snowflakes = [];
    const maxSnowflakes = 200;
    
    for (let i = 0; i < maxSnowflakes; i++) {
      snowflakes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 4 + 1,
        speed: Math.random() * 2 + 0.5,
        wind: Math.random() * 1 - 0.5,
        opacity: Math.random() * 0.6 + 0.3
      });
    }
    
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      snowflakes.forEach((flake) => {
        ctx.beginPath();
        ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${flake.opacity})`;
        ctx.fill();
        
        flake.y += flake.speed;
        flake.x += flake.wind;
        
        if (flake.y > canvas.height) {
          flake.y = -10;
          flake.x = Math.random() * canvas.width;
        }
        if (flake.x > canvas.width) flake.x = 0;
        if (flake.x < 0) flake.x = canvas.width;
      });
    };
    
    const interval = setInterval(draw, 20);
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener("resize", handleResize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  
  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0"
      />
      <div className="fixed top-0 left-0 right-0 h-16 pointer-events-none z-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 rounded-full"
            style={{
              left: `${(i * 5) + 2.5}%`,
              top: '10px',
              background: i % 3 === 0 ? '#ff0000' : i % 3 === 1 ? '#00ff00' : '#ffff00',
              boxShadow: `0 0 10px ${i % 3 === 0 ? '#ff0000' : i % 3 === 1 ? '#00ff00' : '#ffff00'}`,
              animation: `twinkle ${2 + Math.random()}s infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>
    </>
  );
};

// Black Hole Effect
const BlackHole = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    const particles = [];
    for (let i = 0; i < 600; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = 180 + Math.random() * 250;
      particles.push({
        angle: angle,
        distance: distance,
        speed: 0.008 + Math.random() * 0.015,
        size: Math.random() * 2 + 0.3,
        brightness: Math.random()
      });
    }
    
    const stars = [];
    for (let i = 0; i < 200; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5,
        opacity: Math.random() * 0.3 + 0.1
      });
    }
    
    const draw = () => {
      ctx.fillStyle = "#000000";
      ctx.globalAlpha = 0.4;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1.0;
      
      stars.forEach(star => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * 0.5})`;
        ctx.fill();
      });
      
      particles.forEach(p => {
        p.angle += p.speed / p.distance * 100;
        p.distance *= 0.996;
        
        if (p.distance < 140) {
          p.angle = Math.random() * Math.PI * 2;
          p.distance = 180 + Math.random() * 250;
        }
        
        const x = centerX + Math.cos(p.angle) * p.distance;
        const y = centerY + Math.sin(p.angle) * p.distance;
        
        const heatRatio = 1 - (p.distance - 140) / 290;
        const opacity = p.brightness * (0.2 + heatRatio * 0.7);
        
        const r = 255;
        const g = Math.floor(220 + heatRatio * 35);
        const b = Math.floor(180 + heatRatio * 75);
        
        ctx.beginPath();
        ctx.arc(x, y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        ctx.fill();
      });
      
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 140);
      gradient.addColorStop(0, "#000000");
      gradient.addColorStop(0.92, "#000000");
      gradient.addColorStop(0.98, "rgba(255, 240, 200, 0.3)");
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 140, 0, Math.PI * 2);
      ctx.fill();
    };
    
    const interval = setInterval(draw, 25);
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };
    
    window.addEventListener("resize", handleResize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  
  return (
    <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundColor: "#000000" }}>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ backgroundColor: "#000000" }}
      />
    </div>
  );
};

// COSMIC STARS - VIDEO BACKGROUND with Seamless Loop + Smart Loading
const CosmicStars = () => {
  const canvasRef = useRef(null);
  const video1Ref = useRef(null);
  const video2Ref = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  // Crossfade logic for seamless loop
  useEffect(() => {
    const video1 = video1Ref.current;
    const video2 = video2Ref.current;
    if (!video1 || !video2) return;
    
    const crossfadeDuration = 2;
    
    const handleTimeUpdate = (currentVideo, nextVideo) => {
      const timeLeft = currentVideo.duration - currentVideo.currentTime;
      
      if (timeLeft <= crossfadeDuration && timeLeft > 0) {
        const fadeProgress = 1 - (timeLeft / crossfadeDuration);
        currentVideo.style.opacity = 1 - fadeProgress;
        nextVideo.style.opacity = fadeProgress;
        
        if (nextVideo.paused) {
          nextVideo.currentTime = 0;
          nextVideo.play().catch(() => {});
        }
      }
    };
    
    const handleVideo1TimeUpdate = () => handleTimeUpdate(video1, video2);
    const handleVideo2TimeUpdate = () => handleTimeUpdate(video2, video1);
    
    const handleVideo1Ended = () => {
      video1.style.opacity = 0;
      video2.style.opacity = 1;
      video1.currentTime = 0;
    };
    
    const handleVideo2Ended = () => {
      video2.style.opacity = 0;
      video1.style.opacity = 1;
      video2.currentTime = 0;
    };
    
    // Video ready to play
    const handleCanPlay = () => {
      if (!videoLoaded) {
        setVideoLoaded(true);
        video1.play().catch(() => {});
      }
    };
    
    video1.addEventListener('timeupdate', handleVideo1TimeUpdate);
    video2.addEventListener('timeupdate', handleVideo2TimeUpdate);
    video1.addEventListener('ended', handleVideo1Ended);
    video2.addEventListener('ended', handleVideo2Ended);
    video1.addEventListener('canplay', handleCanPlay);
    
    video1.style.opacity = 1;
    video2.style.opacity = 0;
    
    return () => {
      video1.removeEventListener('timeupdate', handleVideo1TimeUpdate);
      video2.removeEventListener('timeupdate', handleVideo2TimeUpdate);
      video1.removeEventListener('ended', handleVideo1Ended);
      video2.removeEventListener('ended', handleVideo2Ended);
      video1.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoLoaded]);
  
  // Canvas stars effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const stars = [];
    for (let i = 0; i < 100; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: 0.5 + Math.random() * 1,
        baseOpacity: 0.3 + Math.random() * 0.4,
        twinkleSpeed: 0.02 + Math.random() * 0.03,
        twinklePhase: Math.random() * Math.PI * 2
      });
    }
    
    const shootingStars = [];
    
    const createShootingStar = () => {
      if (shootingStars.length < 2 && Math.random() > 0.995) {
        shootingStars.push({
          x: Math.random() * canvas.width * 0.5,
          y: Math.random() * canvas.height * 0.3,
          length: 80 + Math.random() * 80,
          speed: 15 + Math.random() * 10,
          angle: Math.PI / 5 + (Math.random() - 0.5) * 0.3,
          opacity: 1,
          life: 0,
          maxLife: 25 + Math.random() * 20
        });
      }
    };
    
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      stars.forEach(star => {
        star.twinklePhase += star.twinkleSpeed;
        const twinkle = (Math.sin(star.twinklePhase) + 1) / 2;
        const opacity = star.baseOpacity * (0.2 + twinkle * 0.8);
        
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.fill();
      });
      
      createShootingStar();
      
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const star = shootingStars[i];
        star.x += Math.cos(star.angle) * star.speed;
        star.y += Math.sin(star.angle) * star.speed;
        star.life++;
        
        if (star.life > star.maxLife * 0.6) {
          star.opacity = 1 - (star.life - star.maxLife * 0.6) / (star.maxLife * 0.4);
        }
        
        const tailX = star.x - Math.cos(star.angle) * star.length;
        const tailY = star.y - Math.sin(star.angle) * star.length;
        
        const gradient = ctx.createLinearGradient(tailX, tailY, star.x, star.y);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(0.7, `rgba(200, 220, 255, ${star.opacity * 0.5})`);
        gradient.addColorStop(1, `rgba(255, 255, 255, ${star.opacity})`);
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(star.x, star.y);
        ctx.stroke();
        
        if (star.life >= star.maxLife || star.x > canvas.width + 50 || star.y > canvas.height + 50) {
          shootingStars.splice(i, 1);
        }
      }
    };
    
    const interval = setInterval(draw, 30);
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener("resize", handleResize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  
  const videoStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: -3,
    filter: 'saturate(1.2) contrast(1.1)',
    transition: 'opacity 0.5s ease-in-out'
  };
  
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {/* Poster/Fallback - Shows immediately while video loads */}
      <div 
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -4,
          backgroundImage: 'url(/images/cosmic-poster.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#030308',
          opacity: videoLoaded ? 0 : 1,
          transition: 'opacity 1s ease-in-out'
        }}
      />
      
      {/* Video Layer 1 */}
      <video
        ref={video1Ref}
        muted
        playsInline
        preload="auto"
        poster="/images/cosmic-poster.webp"
        style={{ ...videoStyle, opacity: videoLoaded ? 1 : 0 }}
      >
        <source src="/images/cosmic.mp4" type="video/mp4" />
      </video>
      
      {/* Video Layer 2 (for crossfade) */}
      <video
        ref={video2Ref}
        muted
        playsInline
        preload="auto"
        style={{ ...videoStyle, opacity: 0 }}
      >
        <source src="/images/cosmic.mp4" type="video/mp4" />
      </video>
      
      {/* Space overlay - vignette */}
      <div 
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -2,
          background: 'radial-gradient(circle at center, rgba(0,0,0,0.15), rgba(0,0,0,0.7))',
          pointerEvents: 'none'
        }}
      />
      
      {/* Canvas for stars */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ zIndex: -1 }}
      />
    </div>
  );
};

// Aurora Effect
const Aurora = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const waves = [
      {
        topPoints: [],
        bottomPoints: [],
        color: { r: 0, g: 220, b: 140 },
        amplitude: 100,
        frequency: 0.002,
        speed: 0.004,
        offset: 0,
        yBase: canvas.height * 0.15,
        height: 350
      },
      {
        topPoints: [],
        bottomPoints: [],
        color: { r: 100, g: 180, b: 255 },
        amplitude: 90,
        frequency: 0.0025,
        speed: 0.005,
        offset: Math.PI / 2,
        yBase: canvas.height * 0.2,
        height: 400
      },
      {
        topPoints: [],
        bottomPoints: [],
        color: { r: 255, g: 100, b: 200 },
        amplitude: 85,
        frequency: 0.0022,
        speed: 0.0045,
        offset: Math.PI,
        yBase: canvas.height * 0.25,
        height: 380
      }
    ];
    
    waves.forEach(wave => {
      for (let x = 0; x <= canvas.width; x += 8) {
        wave.topPoints.push({ x, y: 0 });
        wave.bottomPoints.push({ x, y: 0 });
      }
    });
    
    const stars = [];
    for (let i = 0; i < 120; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height * 0.6,
        size: Math.random() * 2,
        opacity: Math.random() * 0.5 + 0.2,
        twinkleSpeed: 0.02 + Math.random() * 0.03,
        twinklePhase: Math.random() * Math.PI * 2
      });
    }
    
    const draw = () => {
      ctx.fillStyle = "rgba(2, 5, 15, 0.12)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      stars.forEach(star => {
        star.twinklePhase += star.twinkleSpeed;
        const twinkle = (Math.sin(star.twinklePhase) + 1) / 2;
        const opacity = star.opacity * (0.3 + twinkle * 0.7);
        
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.fill();
      });
      
      waves.forEach((wave) => {
        wave.offset += wave.speed;
        
        wave.topPoints.forEach((point) => {
          const x = point.x;
          const y = wave.yBase + 
                    Math.sin(x * wave.frequency + wave.offset) * wave.amplitude +
                    Math.sin(x * wave.frequency * 1.7 + wave.offset * 1.2) * (wave.amplitude * 0.4);
          point.y = y;
        });
        
        wave.bottomPoints.forEach((point) => {
          const x = point.x;
          const bottomY = wave.yBase + wave.height + 
                          Math.sin(x * wave.frequency * 0.8 + wave.offset * 0.6) * (wave.amplitude * 0.6);
          point.y = bottomY;
        });
        
        for (let layer = 0; layer < 3; layer++) {
          const pulseIntensity = 0.6 + Math.sin(wave.offset * 0.4 + layer * 0.5) * 0.4;
          const layerOpacity = (0.3 - layer * 0.08) * pulseIntensity;
          
          const gradient = ctx.createLinearGradient(0, wave.yBase - 50, 0, wave.yBase + wave.height + 50);
          gradient.addColorStop(0, `rgba(${wave.color.r}, ${wave.color.g}, ${wave.color.b}, 0)`);
          gradient.addColorStop(0.4, `rgba(${wave.color.r}, ${wave.color.g}, ${wave.color.b}, ${layerOpacity})`);
          gradient.addColorStop(0.7, `rgba(${wave.color.r}, ${wave.color.g}, ${wave.color.b}, ${layerOpacity * 0.8})`);
          gradient.addColorStop(1, `rgba(${wave.color.r}, ${wave.color.g}, ${wave.color.b}, 0)`);
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          
          ctx.moveTo(wave.topPoints[0].x, wave.topPoints[0].y + layer * 12);
          for (let i = 1; i < wave.topPoints.length; i++) {
            const point = wave.topPoints[i];
            const prevPoint = wave.topPoints[i - 1];
            const cpX = (prevPoint.x + point.x) / 2;
            const cpY = (prevPoint.y + point.y) / 2;
            ctx.quadraticCurveTo(prevPoint.x, prevPoint.y + layer * 12, cpX, cpY + layer * 12);
          }
          
          ctx.lineTo(canvas.width, wave.bottomPoints[wave.bottomPoints.length - 1].y + layer * 12);
          
          for (let i = wave.bottomPoints.length - 1; i >= 0; i--) {
            const point = wave.bottomPoints[i];
            if (i > 0) {
              const prevPoint = wave.bottomPoints[i - 1];
              const cpX = (prevPoint.x + point.x) / 2;
              const cpY = (prevPoint.y + point.y) / 2;
              ctx.quadraticCurveTo(point.x, point.y + layer * 12, cpX, cpY + layer * 12);
            } else {
              ctx.lineTo(point.x, point.y + layer * 12);
            }
          }
          
          ctx.lineTo(0, wave.topPoints[0].y + layer * 12);
          ctx.closePath();
          ctx.fill();
        }
      });
    };
    
    const interval = setInterval(draw, 40);
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      waves.forEach(wave => {
        wave.topPoints = [];
        wave.bottomPoints = [];
        for (let x = 0; x <= canvas.width; x += 8) {
          wave.topPoints.push({ x, y: 0 });
          wave.bottomPoints.push({ x, y: 0 });
        }
      });
    };
    
    window.addEventListener("resize", handleResize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ backgroundColor: "#02050f" }}
    />
  );
};

// Neon City Effect
const NeonCity = () => {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 0, 128, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          animation: "grid-move 20s linear infinite"
        }}
      />
      
      <div 
        className="absolute bottom-0 left-0 right-0 h-[40%]"
        style={{
          background: "linear-gradient(180deg, transparent 0%, rgba(255, 0, 128, 0.15) 40%, rgba(0, 255, 255, 0.2) 100%)"
        }}
      />
      
      {[...Array(8)].map((_, i) => {
        const isVertical = i % 2 === 0;
        const color = i % 3 === 0 ? "#ff0080" : i % 3 === 1 ? "#00ffff" : "#ffff00";
        
        return (
          <div
            key={i}
            className="absolute"
            style={{
              [isVertical ? "width" : "height"]: "2px",
              [isVertical ? "height" : "width"]: `${30 + Math.random() * 40}%`,
              [isVertical ? "left" : "bottom"]: `${10 + i * 11}%`,
              [isVertical ? "bottom" : "left"]: isVertical ? "10%" : `${20 + i * 8}%`,
              background: color,
              boxShadow: `0 0 15px ${color}, 0 0 30px ${color}`,
              animation: `neon-flicker ${2 + Math.random()}s infinite`,
              animationDelay: `${Math.random() * 2}s`,
              opacity: 0.6
            }}
          />
        );
      })}
      
      {[...Array(20)].map((_, i) => (
        <div
          key={`particle-${i}`}
          className="absolute w-1 h-1 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            background: i % 2 === 0 ? "#ff0080" : "#00ffff",
            boxShadow: `0 0 5px ${i % 2 === 0 ? "#ff0080" : "#00ffff"}`,
            animation: `float-particle ${5 + Math.random() * 5}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`
          }}
        />
      ))}
      
      <style>{`
        @keyframes neon-flicker {
          0%, 100% { opacity: 0.6; }
          10% { opacity: 0.3; }
          15% { opacity: 0.8; }
          20% { opacity: 0.5; }
          25% { opacity: 1; }
          30% { opacity: 0.7; }
        }
        @keyframes grid-move {
          0% { transform: translateY(0); }
          100% { transform: translateY(50px); }
        }
        @keyframes float-particle {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-30px) translateX(20px); }
        }
      `}</style>
    </div>
  );
};

const ThemeBackground = () => {
  const { currentTheme } = useTheme();
  const isNebulaFlow = currentTheme === "nebulaflow";
  
  return (
    <div className="theme-bg fixed inset-0 -z-10">
      {currentTheme === "cosmic" && <CosmicStars />}
      {currentTheme === "blackhole" && <BlackHole />}
      {currentTheme === "aurora" && <Aurora />}
      {currentTheme === "matrix" && <MatrixRain />}
      {currentTheme === "neoncity" && <NeonCity />}
      {currentTheme === "christmas" && <Snowfall />}
      {isNebulaFlow && (
        <NebulaFlowCursor />
      )}
    </div>
  );
};

export default ThemeBackground;
